import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Target,
  Heart,
  Award,
  Users,
} from "lucide-react";

export default function AboutPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative border-b border-ink-900 bg-white" data-testid="about-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid opacity-50" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-32">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 bg-accent" />
              <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500">
                /// File 02 — About UMG
              </span>
            </div>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-ink-900 sm:text-7xl lg:text-8xl">
              Pioneering secure
              <br />
              <span className="text-outline">payment solutions</span>
              <br />
              for <span className="text-accent">high-risk</span> merchants.
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-sm leading-relaxed text-ink-500">
              We build the global operating system for fintech — starting with
              payments. Founded 2008. Trusted by 12,500+ merchants worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="border-b border-ink-900 bg-white" data-testid="about-story">
        <div className="container-wide grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <span className="eyebrow-accent">
              <span className="h-2 w-2 bg-accent" /> 01 — Our Story
            </span>
            <h2 className="section-title mt-6">
              Rewriting financial services
              <br />
              <span className="text-accent">one API at a time.</span>
            </h2>
          </div>
          <div className="space-y-8 lg:col-span-7">
            <p className="font-mono text-base leading-relaxed text-ink-700 sm:text-lg">
              Since our founding in 2008, UMG has been on a mission to create
              the most accessible financial services ecosystem in history. We
              are building the global operating system for fintech, starting
              with payments.
            </p>
            <p className="font-mono text-base leading-relaxed text-ink-500">
              Moving money is a fundamental human activity and critical to the
              health of our global economy. However, innovation from
              established financial services providers has failed to keep pace
              with the needs of innovative SaaS platforms and their users. UMG
              exists to lower barriers to entry, drive competition, and make
              the financial system more equitable and accessible for people
              around the world.
            </p>
            <p className="font-mono text-base leading-relaxed text-ink-500">
              Our team of experts is dedicated to staying ahead of the curve in
              the ever-evolving payment processing landscape. By leveraging the
              latest advancements in encryption, fraud detection, and risk
              management techniques, we offer our clients peace of mind, knowing
              their transactions are protected against potential threats.
            </p>

            <div className="grid grid-cols-2 border border-ink-900 sm:grid-cols-4">
              <Metric label="Monthly volume" value="$1.2B+" />
              <Metric label="Uptime" value="99.99%" sep />
              <Metric label="Acquirers" value="20+" sep />
              <Metric label="Currencies" value="150+" sep />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-ink-900 bg-white" data-testid="about-values">
        <div className="container-wide grid items-end gap-10 pb-12 pt-20 lg:grid-cols-12 lg:pt-24">
          <div className="lg:col-span-8">
            <span className="eyebrow-accent">
              <span className="h-2 w-2 bg-accent" /> 02 — Values
            </span>
            <h2 className="section-mega mt-6">
              What drives us
              <br />
              <span className="text-outline">every day.</span>
            </h2>
          </div>
        </div>

        <div className="border-t border-ink-900">
          <div className="container-wide grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Target,
                title: "Mission-Driven",
                body: "Every decision ties back to making payments more accessible for merchants everywhere.",
                n: "01",
              },
              {
                icon: ShieldCheck,
                title: "Security First",
                body: "PCI DSS Level 1 certified with bank-grade encryption and fraud protection.",
                n: "02",
              },
              {
                icon: Heart,
                title: "Customer Obsessed",
                body: "24/7/365 dedicated support from real payments experts who know your industry.",
                n: "03",
              },
              {
                icon: Award,
                title: "Integrity",
                body: "Transparent pricing, honest advice, and relationships built to last.",
                n: "04",
              },
            ].map(({ icon: Icon, title, body, n }, i, arr) => (
              <article
                key={n}
                data-testid={`value-card-${n}`}
                className={`group p-10 border-ink-900 transition-colors duration-200 hover:bg-ink-900 hover:text-white ${
                  (i + 1) % 4 !== 0 ? "lg:border-r" : ""
                } ${(i + 1) % 2 !== 0 ? "md:border-r" : "md:border-r-0"} ${
                  i < arr.length - 1 ? "border-b lg:border-b-0" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500 group-hover:text-white/60">
                    {n}
                  </span>
                  <Icon className="h-6 w-6 group-hover:text-accent" />
                </div>
                <h3 className="mt-10 font-display text-2xl font-black uppercase leading-tight tracking-tighter">
                  {title}
                </h3>
                <p className="mt-5 font-mono text-sm leading-relaxed text-ink-500 group-hover:text-white/70">
                  {body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BELIEFS / CTA */}
      <section className="border-b border-ink-900 bg-white" data-testid="about-beliefs">
        <div className="container-wide grid items-start gap-12 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-6">
            <span className="eyebrow-accent">
              <span className="h-2 w-2 bg-accent" /> 03 — Beliefs
            </span>
            <h2 className="section-title mt-6">
              Every business deserves
              <br />
              the <span className="text-accent">best</span> payment technology.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="font-mono text-base leading-relaxed text-ink-700 sm:text-lg">
              We believe every business should have access to the best and most
              innovative payment technology — and that they should earn the
              bulk of the economic benefit. To accelerate access to payments
              and fintech for the greater good, we remove the technical,
              compliance, regulatory, and global barriers to entry.
            </p>
            <ul className="mt-8 divide-y divide-ink-900 border-y border-ink-900">
              {[
                "Instant merchant onboarding with full KYC handled in-house",
                "Acquirer-agnostic gateway — multiple MIDs under one integration",
                "Weekly card-scheme rule updates baked into our compliance team",
                "Chargeback defender — early warnings turn disputes into refunds",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-4 py-5">
                  <span className="font-mono text-[11px] uppercase tracking-ultra text-accent">
                    0{i + 1}
                  </span>
                  <span className="font-mono text-sm leading-relaxed text-ink-700">
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-ink-900 bg-ink-900 text-white" data-testid="about-cta">
        <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid-dark opacity-50" />
        <div className="container-wide relative z-10 grid items-center gap-8 py-20 lg:grid-cols-[1.6fr_auto] lg:py-24">
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-accent" />
              <span className="font-mono text-[11px] uppercase tracking-ultra text-white/60">
                Join our network
              </span>
            </div>
            <h2 className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
              Ready to build the
              <br />
              future of payments
              <br />
              <span className="text-accent">together?</span>
            </h2>
          </div>
          <div>
            <Link to="/contact-us" className="btn-inverse" data-testid="about-cta-button">
              Get in touch <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Metric({ label, value, sep = false }) {
  return (
    <div className={`p-6 ${sep ? "border-l border-ink-900" : ""}`}>
      <div className="font-display text-3xl font-black tracking-tighter text-ink-900 sm:text-4xl">
        {value}
      </div>
      <div className="mt-3 font-mono text-[11px] uppercase tracking-ultra text-ink-500">
        {label}
      </div>
    </div>
  );
}
